/**
 * Deterministic, canonical fingerprint of the setup configuration a revision
 * was verified against. Two runs of the same semantic setup must produce the
 * same token regardless of object key order, and no wall-clock value may ever
 * take part in it.
 */

import { areFingerprintsEqual, fingerprintPayload, stableJsonStringify } from '../stableFingerprint';

export type ConnectorParameterDestination = 'project' | 'family';
export type ConnectorParameterBindingKind = 'type' | 'instance';

/** Authoritative identity of the Revit document a revision belongs to. */
export interface SetupDocumentIdentity {
  projectKey: string;
  documentTitle: string;
  documentPath: string;
}

/** Two identities name the same document only when every part agrees. */
export const areSetupDocumentIdentitiesEqual = (
  left: SetupDocumentIdentity,
  right: SetupDocumentIdentity
): boolean =>
  left.projectKey === right.projectKey &&
  left.documentTitle === right.documentTitle &&
  left.documentPath === right.documentPath;

export interface SetupFingerprintInput {
  identity: SetupDocumentIdentity;
  parameterDestination: ConnectorParameterDestination | null;
  parameterBindingKind: ConnectorParameterBindingKind | null;
  /** Setup/profile fields that affect mapping readiness or Validation. */
  setupFields?: Record<string, unknown> | null;
}

const FingerprintPrefix = 'sr1';

export const canonicalSetupPayload = (input: SetupFingerprintInput): string =>
  stableJsonStringify({
    projectKey: input.identity.projectKey,
    documentTitle: input.identity.documentTitle,
    documentPath: input.identity.documentPath,
    parameterDestination: input.parameterDestination,
    parameterBindingKind: input.parameterBindingKind,
    setupFields: input.setupFields ?? {},
  });

export const computeSetupFingerprint = (input: SetupFingerprintInput): string =>
  fingerprintPayload(FingerprintPrefix, canonicalSetupPayload(input));

/**
 * Fingerprint comparison for trust decisions: an absent or empty fingerprint is
 * never evidence that two revisions agree.
 */
export const areSetupFingerprintsEqual = areFingerprintsEqual;
